import _styled from "styled-components";
import { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { SadFaceIcon, useResponsiveBreakPoints, getDurationSinceDate } from '@restworld/utils-common';
import { NewCandidateNotification } from '../employer-card';
import { Title } from '../title';
import { V2Link } from '../../feature-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function EmployerActivityCard(props) {
  var candidateCount = props.candidateCount,
      restaurantName = props.restaurantName,
      city = props.city,
      jobPositionTitle = props.jobPositionTitle,
      dateSinceLastActivity = props.dateSinceLastActivity,
      href = props.href,
      _props$candidates = props.candidates,
      candidates = _props$candidates === void 0 ? [] : _props$candidates,
      _props$isFirst = props.isFirst,
      isFirst = _props$isFirst === void 0 ? true : _props$isFirst,
      _props$isLast = props.isLast,
      isLast = _props$isLast === void 0 ? true : _props$isLast,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'new_shortlist_applications' : _props$variant;

  var _useTranslation = useTranslation('common'),
      t = _useTranslation.t;

  var headingEl = useActivityCardHeading({
    variant: variant,
    candidateCount: candidateCount,
    jobPositionTitle: jobPositionTitle,
    restaurantName: restaurantName,
    candidates: candidates
  });

  var _useState = useState(false),
      isHovered = _useState[0],
      setIsHovered = _useState[1];

  var _useResponsiveBreakPo = useResponsiveBreakPoints(),
      isMobile = _useResponsiveBreakPo.isMobile;

  var _useMemo = useMemo(function () {
    return getDurationSinceDate(dateSinceLastActivity);
  }, [dateSinceLastActivity]),
      timeSinceLastActivity = _useMemo.duration,
      unit = _useMemo.unit;

  var timeAgoText = useMemo(function () {
    return timeSinceLastActivity > 1 ? t(unit === 'months' ? 'months_key' : unit === 'days' ? 'days_key' : unit === 'hours' ? 'hours_key' : 'minutes_key') : t(unit === 'months' ? 'month_key' : unit === 'days' ? 'day_key' : unit === 'hours' ? 'hour_key' : 'minute_key');
  }, [timeSinceLastActivity, t, unit]);
  var candidateDisplayMax = 3;
  var currentCandidateCount = useMemo(function () {
    return candidateCount <= candidateDisplayMax ? candidateCount : candidateDisplayMax;
  }, [candidateCount]);
  var currentCandidates = useMemo(function () {
    return candidates.slice(0, candidateDisplayMax);
  }, [candidates]);
  return /*#__PURE__*/_jsx(V2Link, {
    href: href || '',
    children: /*#__PURE__*/_jsxs(MainWrapper, {
      isLast: isLast,
      isFirst: isFirst,
      onMouseEnter: function onMouseEnter() {
        return setIsHovered(true);
      },
      onMouseLeave: function onMouseLeave() {
        return setIsHovered(false);
      },
      isHovered: isHovered,
      children: [/*#__PURE__*/_jsxs(ContentWrapper, {
        children: [/*#__PURE__*/_jsx(TitleWrapper, {
          children: headingEl
        }), /*#__PURE__*/_jsxs(BottomContentWrapper, {
          children: [/*#__PURE__*/_jsx("div", {
            children: variant === 'active_applications_without_feedback' ? /*#__PURE__*/_jsx(SadFaceIconWrapper, {
              children: /*#__PURE__*/_jsx(_StyledSadFaceIcon, {})
            }) : /*#__PURE__*/_jsx(NewCandidateNotification, {
              wrapperStyle: {
                "width": "4rem",
                "minWidth": "64px",
                "backgroundColor": "transparent"
              },
              imageWrapperStyle: variant === 'spontaneous_applications' ? {
                "--tw-bg-opacity": "1",
                "backgroundColor": "rgb(158 84 0 / var(--tw-bg-opacity))"
              } : {
                "--tw-bg-opacity": "1",
                "backgroundColor": "rgb(72 36 133 / var(--tw-bg-opacity))"
              },
              candidateCount: currentCandidateCount,
              candidates: currentCandidates
            })
          }), /*#__PURE__*/_jsx(Title, {
            icon: "BuildingOffice2Icon",
            twStyle: {
              "fontSize": "0.875rem",
              "lineHeight": "1.25rem"
            },
            textColor: "neutral-100",
            fontWeight: "normal",
            children: variant === 'spontaneous_applications' ? city : "".concat(restaurantName, ", ").concat(city)
          })]
        })]
      }), /*#__PURE__*/_jsx(StatusWrapper, {
        children: isHovered && !isMobile ? /*#__PURE__*/_jsx(_StyledArrowRightIcon, {}) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(Title, {
            twStyle: {
              "fontSize": "0.875rem",
              "lineHeight": "1.25rem"
            },
            textColor: "neutral-100",
            fontWeight: "normal",
            children: [timeSinceLastActivity, " ", timeAgoText, " ", t('ago')]
          }), /*#__PURE__*/_jsx(Indicator, {
            variant: variant === 'active_applications_without_feedback' ? 'error' : 'default'
          })]
        })
      })]
    })
  });
}
export default EmployerActivityCard;

function useActivityCardHeading(_ref) {
  var variant = _ref.variant,
      candidateCount = _ref.candidateCount,
      jobPositionTitle = _ref.jobPositionTitle,
      restaurantName = _ref.restaurantName,
      candidates = _ref.candidates;

  var _useTranslation2 = useTranslation('common'),
      t = _useTranslation2.t;

  var isSingleCandidate = candidateCount === 1;
  var defaultVariant = useMemo(function () {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [t(isSingleCandidate ? 'there_is_key' : 'there_are'), ' ', /*#__PURE__*/_jsxs(PurpleText, {
        children: [candidateCount, " ", t(isSingleCandidate ? 'new_candidate_key' : 'new_candidates')]
      }), ' ', t('in_the_shortlist_for'), " ", /*#__PURE__*/_jsx(NeutralText, {
        children: jobPositionTitle
      }), ' ', /*#__PURE__*/_jsx("span", {
        role: "img",
        "aria-label": "party popper",
        children: "\uD83C\uDF89"
      })]
    });
  }, [candidateCount, isSingleCandidate, jobPositionTitle, t]);
  var spontaneousVariant = useMemo(function () {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(NeutralText, {
        children: [candidateCount, " ", t(isSingleCandidate ? 'new_spontaneous_candidate' : 'new_spontaneous_candidates')]
      }), ' ', t('applied_to_work_at'), " ", /*#__PURE__*/_jsx(NeutralText, {
        children: restaurantName
      }), ' ', /*#__PURE__*/_jsx("span", {
        role: "img",
        "aria-label": "eyes",
        children: "\uD83D\uDC40"
      })]
    });
  }, [candidateCount, isSingleCandidate, restaurantName, t]);
  var multipleCandidatesVariantText = useMemo(function () {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [t('and'), " ", /*#__PURE__*/_jsx(NeutralText, {
        children: t('a_number_of_others', {
          count: candidateCount - 1
        })
      }), ' ', t('are_waiting_for_your_feedback')]
    });
  }, [candidateCount, t]);
  var singleCandidateVariantText = useMemo(function () {
    return t('is_waiting_for_your_feedback');
  }, [t]);
  var noNameVariantText = useMemo(function () {
    return t(isSingleCandidate ? 'a_candidate_is_waiting_for_your_feedback' : 'candidates_are_waiting_for_your_feedback', {
      count: candidateCount
    });
  }, [candidateCount, isSingleCandidate, t]);
  var lastCheckLongAgoVariant = useMemo(function () {
    var _candidates$, _candidates$2;

    var nameOfCandidate = candidates !== null && candidates !== void 0 && candidates.length ? "".concat((_candidates$ = candidates[0]) === null || _candidates$ === void 0 ? void 0 : _candidates$.firstName, " ").concat((_candidates$2 = candidates[0]) === null || _candidates$2 === void 0 ? void 0 : _candidates$2.lastName) : '';
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [t('you_havent_checked_the_position'), " ", /*#__PURE__*/_jsx(NeutralText, {
        children: jobPositionTitle
      }), " ", t('for_a_while'), ",", ' ', /*#__PURE__*/_jsx(NeutralText, {
        children: nameOfCandidate
      }), ' ', !nameOfCandidate ? noNameVariantText : isSingleCandidate ? singleCandidateVariantText : multipleCandidatesVariantText]
    });
  }, [candidates, isSingleCandidate, jobPositionTitle, multipleCandidatesVariantText, noNameVariantText, singleCandidateVariantText, t]);
  var currentVariant = useMemo(function () {
    return variant === 'spontaneous_applications' ? spontaneousVariant : variant === 'active_applications_without_feedback' ? lastCheckLongAgoVariant : defaultVariant;
  }, [defaultVariant, lastCheckLongAgoVariant, spontaneousVariant, variant]);
  return /*#__PURE__*/_jsx(Title, {
    fontWeight: "semibold",
    fontSize: "base",
    textColor: "neutral-200",
    twStyle: {
      "overflow": "hidden",
      "display": "-webkit-box",
      "WebkitBoxOrient": "vertical",
      "WebkitLineClamp": "3",
      "@media (min-width: 768px)": {
        "overflow": "hidden",
        "display": "-webkit-box",
        "WebkitBoxOrient": "vertical",
        "WebkitLineClamp": "2"
      }
    },
    children: currentVariant
  });
}

var MainWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-activity-card__MainWrapper",
  componentId: "sc-1lli5hq-0"
})(["", " ", " ", " ", ""], {
  "display": "flex",
  "width": "100%",
  "flexDirection": "column-reverse",
  "borderBottomWidth": "1px",
  "--tw-border-opacity": "1",
  "borderColor": "rgb(222 222 225 / var(--tw-border-opacity))",
  "padding": "1rem",
  "paddingBottom": "1.5rem",
  "transitionProperty": "all",
  "transitionTimingFunction": "cubic-bezier(0.4, 0, 0.2, 1)",
  "transitionDuration": "150ms",
  "@media (min-width: 768px)": {
    "flexDirection": "row",
    "alignItems": "center",
    "justifyContent": "space-between",
    "gap": "1.25rem",
    "padding": "2rem"
  }
}, function (_ref2) {
  var isFirst = _ref2.isFirst;
  return isFirst ? {
    "borderTopLeftRadius": "0.5rem",
    "borderTopRightRadius": "0.5rem"
  } : {};
}, function (_ref3) {
  var isLast = _ref3.isLast;
  return isLast ? {
    "borderBottomRightRadius": "0.5rem",
    "borderBottomLeftRadius": "0.5rem",
    "borderBottomWidth": "0px"
  } : {};
}, function (_ref4) {
  var isHovered = _ref4.isHovered;
  return isHovered ? {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(235 235 237 / var(--tw-bg-opacity))"
  } : {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(250 250 250 / var(--tw-bg-opacity))"
  };
});

var ContentWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-activity-card__ContentWrapper",
  componentId: "sc-1lli5hq-1"
})(["", ""], {
  "flex": "4"
});

var StatusWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-activity-card__StatusWrapper",
  componentId: "sc-1lli5hq-2"
})(["", ""], {
  "marginBottom": "1rem",
  "display": "flex",
  "flex": "1 1 0%",
  "alignItems": "center",
  "justifyContent": "space-between",
  "gap": "0.75rem",
  "@media (min-width: 768px)": {
    "marginBottom": "0px",
    "justifyContent": "flex-end"
  }
});

var PurpleText = /*#__PURE__*/_styled.span.withConfig({
  displayName: "employer-activity-card__PurpleText",
  componentId: "sc-1lli5hq-3"
})(["", ""], {
  "--tw-text-opacity": "1",
  "color": "rgb(72 36 133 / var(--tw-text-opacity))"
});

var NeutralText = /*#__PURE__*/_styled.span.withConfig({
  displayName: "employer-activity-card__NeutralText",
  componentId: "sc-1lli5hq-4"
})(["", ""], {
  "--tw-text-opacity": "1",
  "color": "rgb(28 28 51 / var(--tw-text-opacity))"
});

var Indicator = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-activity-card__Indicator",
  componentId: "sc-1lli5hq-5"
})(["", " ", ""], {
  "height": "0.75rem",
  "width": "0.75rem",
  "borderRadius": "9999px"
}, function (_ref5) {
  var variant = _ref5.variant;
  return variant === 'error' ? {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(204 0 34 / var(--tw-bg-opacity))"
  } : {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(102 51 187 / var(--tw-bg-opacity))"
  };
});

var TitleWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-activity-card__TitleWrapper",
  componentId: "sc-1lli5hq-6"
})(["", ""], {
  "marginBottom": "1rem",
  "@media (min-width: 768px)": {
    "marginBottom": "1.25rem"
  }
});

var BottomContentWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-activity-card__BottomContentWrapper",
  componentId: "sc-1lli5hq-7"
})(["", ""], {
  "display": "flex",
  "gap": "0.75rem"
});

var SadFaceIconWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-activity-card__SadFaceIconWrapper",
  componentId: "sc-1lli5hq-8"
})(["", ""], {
  "width": "3rem"
});

var _StyledSadFaceIcon = /*#__PURE__*/_styled(SadFaceIcon).withConfig({
  displayName: "employer-activity-card___StyledSadFaceIcon",
  componentId: "sc-1lli5hq-9"
})({
  "fill": "#910018"
});

var _StyledArrowRightIcon = /*#__PURE__*/_styled(ArrowRightIcon).withConfig({
  displayName: "employer-activity-card___StyledArrowRightIcon",
  componentId: "sc-1lli5hq-10"
})({
  "height": "1.5rem",
  "width": "1.5rem",
  "--tw-text-opacity": "1",
  "color": "rgb(28 28 51 / var(--tw-text-opacity))"
});